'use client';

import { useMemo, useState } from 'react';
import { IoChevronBack, IoChevronForward, IoClose } from 'react-icons/io5';
import { BsSliders } from 'react-icons/bs';
import { FaCheckCircle } from 'react-icons/fa';

// Components
import CardJob from '@src/components/atoms/card-job';
import ModalFilterKarir from '@src/components/atoms/modal-filter-karir';
import SkeletonLoader from '@src/components/atoms/skeleton-loader';

// Apis
import { useQueryJobVacancies, useQueryJobVacancyFilters } from '@src/apis/karir';

// Helper
import formatJobVacancy from '@src/helper/formatJobVacancy';

const PER_PAGE = 16;

const getOfficeKey = (location, companyId) => `${location}-${companyId}`;

const getPageNumbers = (current, total) => {
  if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 3) return [1, 2, 3, '...', total];
  if (current >= total - 2) return [1, '...', total - 2, total - 1, total];
  return [1, '...', current, '...', total];
};

const Pagination = ({ page, totalPage, onChange }) => {
  if (totalPage <= 1) return null;

  const navClass =
    'w-[24px] h-[24px] flex items-center justify-center rounded-[2px] text-[#252526] disabled:text-[#C4C4C4] disabled:cursor-not-allowed';

  return (
    <div className="flex items-center justify-center gap-[4px]">
      <button
        className={navClass}
        disabled={page === 1}
        onClick={() => onChange(page - 1)}
      >
        <IoChevronBack size={14} />
      </button>
      {getPageNumbers(page, totalPage).map((p, i) =>
        p === '...' ? (
          <span
            key={`ellipsis-${i}`}
            className="w-[24px] h-[24px] flex items-center justify-center font-satoshi text-[11px] text-[#686A68]"
          >
            ...
          </span>
        ) : (
          <button
            key={`page-${p}`}
            className={`w-[24px] h-[24px] flex items-center justify-center rounded-[2px] font-satoshi text-[11px] ${
              p === page ? 'bg-[#141415] text-[#FAFAFA]' : 'text-[#252526] hover:bg-[#F2F2F2]'
            }`}
            onClick={() => onChange(p)}
          >
            {p}
          </button>
        )
      )}
      <button
        className={navClass}
        disabled={page === totalPage}
        onClick={() => onChange(page + 1)}
      >
        <IoChevronForward size={14} />
      </button>
    </div>
  );
};

const ListJob = () => {
  const [page, setPage] = useState(1);
  const [isOpenFilter, setIsOpenFilter] = useState(false);
  const [selectedOffices, setSelectedOffices] = useState([]);
  const [jobVacancyParams, setJobVacancyParams] = useState({});

  const { data: allJobVacancies } = useQueryJobVacancies();
  const { data: jobVacancyFilters } = useQueryJobVacancyFilters();

  const allJobs = useMemo(
    () => (allJobVacancies?.data?.data ?? []).map(formatJobVacancy),
    [allJobVacancies]
  );

  const filterLocations = useMemo(
    () =>
      (jobVacancyFilters?.data?.data ?? []).map((location) => ({
        id: location.office_location,
        name: location.office_location,
        offices: (location.offices ?? []).map((office) => {
          const id = getOfficeKey(location.office_location, office.company_id);
          return {
            id,
            companyId: office.company_id,
            name: office.name,
            total: allJobs.filter((job) => job.company?.id === office.company_id).length,
          };
        }),
      })),
    [jobVacancyFilters, allJobs]
  );

  const { data: jobVacancies, isLoading } = useQueryJobVacancies(jobVacancyParams);

  const filteredJobs = useMemo(
    () => (jobVacancies?.data?.data ?? []).map(formatJobVacancy),
    [jobVacancies]
  );

  const totalPage = Math.ceil(filteredJobs.length / PER_PAGE);
  const jobs = useMemo(
    () => filteredJobs.slice((page - 1) * PER_PAGE, page * PER_PAGE),
    [filteredJobs, page]
  );

  const filterChips = useMemo(
    () =>
      filterLocations.flatMap((location) => {
        const ids = location.offices.map((office) => office.id).filter((id) => selectedOffices.includes(id));
        return ids.length ? [{ id: location.id, label: location.name, ids }] : [];
      }),
    [filterLocations, selectedOffices]
  );

  const applySelectedOffices = (offices) => {
    const company_ids = [
      ...new Set(
        filterLocations.flatMap((location) =>
          location.offices.filter((office) => offices.includes(office.id)).map((office) => office.companyId)
        )
      ),
    ];

    setSelectedOffices(offices);
    setJobVacancyParams(company_ids.length ? { 'company_ids[]': company_ids } : {});
    setPage(1);
  };

  const handleRemoveChip = (ids) => {
    applySelectedOffices(selectedOffices.filter((id) => !ids.includes(id)));
  };

  const handleApplyFilter = (offices) => {
    applySelectedOffices(offices);
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div
      className="mt-0 mb-0 mx-auto py-[24px] w-full max-w-[1376px]
      dd3-2xl:max-w-[unset] dd2-2xl:px-[64px]
      dd3-xl:px-[40px]
      dd3-lg:px-[32px]
      dd3-md:px-[24px]"
    >
      <div className="flex items-center justify-between mb-[16px]">
        <button
          onClick={() => setIsOpenFilter(true)}
          className={`relative h-[32px] py-0 px-[12px] flex items-center gap-[12px] border border-solid border-[#252526] bg-white text-[14px] text-[#252526] hover:bg-[#F2F2F2] ${
            selectedOffices.length ? 'rounded-[40px] font-satoshi-bold' : 'rounded-[2px] font-satoshi'
          }`}>
          Filter
          <BsSliders className="w-[16px] h-[16px] text-[#252526]" />
          {selectedOffices.length > 0 && (
            <FaCheckCircle className="absolute -top-[8px] -right-[8px] w-[18px] h-[18px] text-[#3B9B60] bg-white rounded-full" />
          )}
        </button>
        <p className="font-satoshi text-[13px] text-[#686A68]">
          Tersedia {filteredJobs.length} lowongan
        </p>
      </div>

      {filterChips.length > 0 && (
        <div className="flex flex-wrap items-center gap-[8px] mb-[16px]">
          {filterChips.map((chip) => (
            <div
              key={`chip-${chip.id}`}
              className="h-[24px] py-0 px-[12px] flex items-center gap-[8px] rounded-[40px] bg-[#F5F5F5] font-satoshi-bold text-[11px] text-[#252526]"
            >
              {chip.label}
              <button
                type="button"
                aria-label={`Hapus filter ${chip.label}`}
                onClick={() => handleRemoveChip(chip.ids)}
                className="flex items-center justify-center"
              >
                <IoClose className="w-[12px] h-[12px] text-[#686A68]" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div
        className="grid grid-cols-4 gap-x-6 gap-y-8 mb-[32px]
        dd3-2xl:grid-cols-3
        dd3-xl:grid-cols-2 dd3-xl:gap-x-4
        dd3-md:grid-cols-1"
      >
        {isLoading
          ? Array.from({ length: 8 }, (_, i) => (
              <SkeletonLoader key={`skeleton-job-${i}`} isShow className="h-[200px] rounded-[4px]" />
            ))
          : jobs.map((job) => (
              <CardJob key={`job-${job.id}`} job={job} href={`/karir/${job.id}`} />
            ))}
      </div>

      {!isLoading && !filteredJobs.length && (
        <p className="font-satoshi text-[14px] text-[#686A68] text-center mb-[32px]">
          Belum ada lowongan tersedia
        </p>
      )}

      <Pagination page={page} totalPage={totalPage} onChange={handleChangePage} />

      <ModalFilterKarir
        isOpen={isOpenFilter}
        onClose={() => setIsOpenFilter(false)}
        onApply={handleApplyFilter}
        locations={filterLocations}
        value={selectedOffices}
      />
    </div>
  );
};

export default ListJob;
